// Registers the app-shell service worker (production builds only). A waiting version (found by this page, another tab
// or the browser's own update check) is applied AUTOMATICALLY (owner request 2026-10-09) at a safe moment, never in
// the middle of typing: at once while the app is hidden/in the background, otherwise after the user has been idle
// for IDLE_MS with no text field focused. Local edits are already durable in IndexedDB (the outbox survives the
// reload). The "update ready" banner stays as the visible state and manual shortcut.
import { ref, watch as vueWatch } from 'vue';

export const updateReady = ref(false);
let reg: ServiceWorkerRegistration | null = null;
if (typeof window !== 'undefined') window.addEventListener('tp-db-blocking', () => { updateReady.value = true; });

function watch(r: ServiceWorkerRegistration): void {
  const consider = (w: ServiceWorker | null) => {
    if (!w) return;
    const check = () => { if (w.state === 'installed' && r.active) updateReady.value = true; };
    check();
    w.addEventListener('statechange', check);
  };
  if (r.waiting && r.active) updateReady.value = true;
  consider(r.installing);
  r.addEventListener('updatefound', () => consider(r.installing));
}

const STARTUP_MS = 10_000;
/** Visible app: apply after this long without pointer/keyboard/scroll activity. */
export const IDLE_MS = 20_000;
/** How often an open app looks for a new version. */
const CHECK_MS = 5 * 60_000;

let lastActivity = Date.now();
/** True while the user is typing in a field (input, textarea, contenteditable): never reload then. */
export function editingNow(doc: Document = document): boolean {
  const el = doc.activeElement as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable === true || el.getAttribute('contenteditable') === 'true');
}
/** Whether a waiting update may be applied now (exported for tests). */
export function safeToApply(now: number, last: number, hidden: boolean, editing: boolean): boolean {
  return hidden || (!editing && now - last >= IDLE_MS);
}
let autoTimer: ReturnType<typeof setInterval> | null = null;
function startAutoApply(): void {
  if (autoTimer || typeof window === 'undefined') return;
  const tryApply = () => {
    if (!updateReady.value) return;
    if (safeToApply(Date.now(), lastActivity, document.visibilityState === 'hidden', editingNow())) { stopAutoApply(); applyUpdate(); }
  };
  autoTimer = setInterval(tryApply, 2_000);
  document.addEventListener('visibilitychange', tryApply);
  tryApply();
}
function stopAutoApply(): void { if (autoTimer) { clearInterval(autoTimer); autoTimer = null; } }
export function registerServiceWorker(): void {
  if (import.meta.env.DEV || !('serviceWorker' in navigator) || !/^https?:$/.test(location.protocol)) return;
  const bootAt = Date.now();
  let touched = false;
  const touch = () => { touched = true; };
  for (const ev of ['pointerdown', 'keydown'] as const) window.addEventListener(ev, touch, { once: true, capture: true });
  for (const ev of ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const) window.addEventListener(ev, () => { lastActivity = Date.now(); }, { passive: true, capture: true });
  vueWatch(updateReady, v => { if (v) startAutoApply(); }, { immediate: true });
  void navigator.serviceWorker.register('/sw.js', { scope: '/' }).then(r => {
    reg = r; watch(r);
    // Just opened and not touched yet (nothing is being edited; queued edits are durable in IndexedDB): take a waiting
    // version now, otherwise a plain reload would keep showing the old one until every tab of the site is closed.
    const early = () => !touched && Date.now() - bootAt < STARTUP_MS && !!r.waiting && !!r.active;
    if (early()) applyUpdate();
    else r.addEventListener('updatefound', () => r.installing?.addEventListener('statechange', () => { if (early()) applyUpdate(); }));
    // Open apps look for a new version every 5 min, when they come back into view and when the connection returns.
    const check = () => void r.update().catch(() => undefined);
    setInterval(check, CHECK_MS);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') check(); });
    window.addEventListener('online', check);
    window.addEventListener('focus', check);
  }).catch(() => undefined);
}

/** Activates the waiting version and reloads (automatic at a safe moment, or from the update-ready banner). */
export function applyUpdate(): void {
  const waiting = reg?.waiting;
  if (!waiting) { location.reload(); return; } // e.g. a newer tab upgraded the local database
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => { if (!reloaded) { reloaded = true; location.reload(); } });
  waiting.postMessage('skip-waiting');
  // An uncontrolled page (first visit) gets no controllerchange: reload once the new worker is active.
  waiting.addEventListener('statechange', () => { if (waiting.state === 'activated' && !reloaded) { reloaded = true; location.reload(); } });
}

/** Ask the browser to keep IndexedDB data (not guaranteed; users/platform may still clear it). */
export async function requestPersistentStorage(): Promise<boolean> {
  try { return navigator.storage && navigator.storage.persist ? await navigator.storage.persist() : false; } catch { return false; }
}
